import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, X, MapPin, Sparkles, Search } from "lucide-react";
import { api } from "@/lib/api";
import { IcebreakerCard } from "@/components/Icebreaker";
import { SpotlightStrip, FantasyFilter, QuizCta } from "@/components/DiscoverExtras";
import { getTheme } from "@/lib/themes";

const hexRgba = (h, a) => { try { const n = h.replace("#", ""); const b = n.length === 3 ? n.split("").map((x) => x + x).join("") : n; return `rgba(${parseInt(b.slice(0, 2), 16)},${parseInt(b.slice(2, 4), 16)},${parseInt(b.slice(4, 6), 16)},${a})`; } catch { return h; } };

const FALLBACK = [
  "https://images.unsplash.com/photo-1769650795569-e51a6e9580c3",
  "https://images.unsplash.com/photo-1760512992083-8e3ab2671ef7",
];

function Card({ profile, onSwipe, active, index }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const likeOpacity = useTransform(x, [40, 140], [0, 1]);
  const nopeOpacity = useTransform(x, [-140, -40], [1, 0]);
  const photo = profile.photos?.[0] || FALLBACK[index % FALLBACK.length];
  const t = getTheme(profile.theme);
  const acc = profile.skin_accent || t.ring;

  return (
    <motion.div
      data-testid={`swipe-card-${profile.id}`}
      className="absolute inset-0 rounded-3xl overflow-hidden cursor-grab active:cursor-grabbing"
      style={{ x, rotate, zIndex: 10 - index, border: `1.5px solid ${hexRgba(acc, 0.6)}`, boxShadow: active ? `0 12px 48px ${hexRgba(acc, 0.35)}` : "none" }}
      drag={active ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={(e, info) => {
        if (info.offset.x > 120) onSwipe(profile, "like");
        else if (info.offset.x < -120) onSwipe(profile, "pass");
      }}
      initial={{ scale: 0.95, y: 20 }}
      animate={{ scale: active ? 1 : 0.95, y: active ? 0 : 20 }}
    >
      <img src={photo} alt={profile.name} className="w-full h-full object-cover" draggable={false} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.1) 45%, transparent)" }} />
      <motion.div style={{ opacity: likeOpacity }} className="absolute top-8 left-8 rotate-[-15deg] border-2 border-[#D4AF37] text-[#D4AF37] font-heading text-3xl px-4 py-1 rounded-lg">LIKE</motion.div>
      <motion.div style={{ opacity: nopeOpacity }} className="absolute top-8 right-8 rotate-[15deg] border-2 border-[#E63946] text-[#E63946] font-heading text-3xl px-4 py-1 rounded-lg">PASS</motion.div>
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-end gap-2">
          <h3 className="font-heading text-4xl text-white">{profile.name}</h3>
          {profile.age && <span className="text-2xl font-light mb-1" style={{ color: acc }}>{profile.age}</span>}
        </div>
        <div className="flex items-center gap-3 text-sm text-[#A0A0A0] mt-1">
          {profile.location && <span className="flex items-center gap-1"><MapPin size={14} /> {profile.location}</span>}
          {profile.gender && <span>· {profile.gender}</span>}
        </div>
        {profile.compatibility != null && (
          <div data-testid={`card-compat-${profile.id}`} className="mt-2 mr-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full text-black gold-gradient">
            {profile.compatibility}% compatible
          </div>
        )}
        {profile.shared_fantasies?.length > 0 && (
          <div data-testid={`card-shared-fantasy-${profile.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full"
            style={{ background: hexRgba(acc, 0.18), color: acc, border: `1px solid ${hexRgba(acc, 0.5)}` }}>
            <span>✨</span>
            Shares {profile.shared_fantasies.length} of your {profile.shared_fantasies.length === 1 ? "fantasy" : "fantasies"}
          </div>
        )}
        {profile.looking_for?.length > 0 && (
          <div data-testid={`card-looking-${profile.id}`} className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-[11px] uppercase tracking-wide flex items-center gap-1" style={{ color: hexRgba(acc, 0.85) }}><Search size={12} /> Looking for</span>
            {profile.looking_for.slice(0, 3).map((l) => (
              <span key={l} className="text-xs px-2.5 py-0.5 rounded-full" style={{ background: hexRgba(acc, 0.15), color: acc }}>{l}</span>
            ))}
          </div>
        )}
        {(() => {
          const p = profile.prompts?.find((x) => (x.a || "").trim());
          return p ? (
            <div data-testid={`card-prompt-${profile.id}`} className="mt-2">
              <p className="text-[11px] text-[#A0A0A0]">{p.q}</p>
              <p className="text-sm text-white/90 italic line-clamp-2">"{p.a}"</p>
            </div>
          ) : (profile.bio && <p className="text-sm text-white/80 mt-2 line-clamp-2">{profile.bio}</p>);
        })()}
        {profile.interests?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {profile.interests.slice(0, 4).map((t) => (
              <span key={t} className="text-xs px-3 py-1 rounded-full border" style={{ borderColor: hexRgba(acc, 0.4), color: acc }}>{t}</span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function Discover() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likesCount, setLikesCount] = useState(0);
  const [craving, setCraving] = useState([]);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    const params = craving.length ? { fantasies: craving.join(",") } : {};
    api.get("/discover", { params }).then((r) => setProfiles(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/likes-you").then((r) => setLikesCount(r.data.length)).catch(() => {}); }, []);
  useEffect(load, [craving]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwipe = async (profile, direction) => {
    setProfiles((prev) => prev.filter((p) => p.id !== profile.id));
    try {
      const { data } = await api.post("/swipe", { target_id: profile.id, direction });
      if (data.matched) {
        toast.success(`💛 It's a match with ${profile.name}!`, {
          action: { label: "Message", onClick: () => navigate(`/chat/${data.match_id}`) },
        });
      }
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="p-6 md:p-12 max-w-xl mx-auto">
      <div className="overline mb-1">Tonight in the Empire</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-8">Discover</h2>

      <IcebreakerCard />
      <QuizCta />
      <SpotlightStrip />
      <FantasyFilter selected={craving} onChange={setCraving} />

      {likesCount > 0 && (
        <button data-testid="likes-you-banner" onClick={() => navigate("/likes")}
          className="w-full mb-6 rounded-2xl p-4 flex items-center justify-between gold-gradient text-black">
          <span className="font-medium flex items-center gap-2"><Heart size={18} fill="currentColor" /> {likesCount} {likesCount === 1 ? "person likes" : "people like"} you</span>
          <span className="text-sm font-semibold">See who →</span>
        </button>
      )}

      <div className="relative w-full" style={{ height: "70vh", maxHeight: 620 }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center text-[#A0A0A0]">Finding desirable members…</div>
        ) : profiles.length === 0 ? (
          <div className="absolute inset-0 pe-card rounded-3xl flex flex-col items-center justify-center text-center p-8">
            <Sparkles className="text-[#D4AF37] mb-4" size={40} />
            <h3 className="font-heading text-2xl mb-2">{craving.length ? "No one matches that craving yet" : "You've seen everyone for now"}</h3>
            <p className="text-sm text-[#A0A0A0] mb-6">{craving.length ? "Try another fantasy or clear the filter." : "Check back soon — new members join the Empire daily."}</p>
            <button data-testid="reload-discover" onClick={load} className="btn-outline-gold px-6 py-2 rounded-full text-sm">Refresh</button>
          </div>
        ) : (
          <AnimatePresence>
            {profiles.slice(0, 3).reverse().map((p, i, arr) => {
              const realIndex = arr.length - 1 - i;
              return <Card key={p.id} profile={p} index={realIndex} active={realIndex === 0} onSwipe={handleSwipe} />;
            })}
          </AnimatePresence>
        )}
      </div>

      {profiles.length > 0 && (
        <div className="flex justify-center gap-6 mt-8">
          <button data-testid="pass-btn" onClick={() => handleSwipe(profiles[0], "pass")}
            className="w-16 h-16 rounded-full border border-[#E63946] flex items-center justify-center text-[#E63946] hover:bg-[#E63946]/10 transition-colors">
            <X size={28} />
          </button>
          <button data-testid="like-btn" onClick={() => handleSwipe(profiles[0], "like")}
            className="w-16 h-16 rounded-full gold-gradient flex items-center justify-center text-black hover:brightness-110 transition-all">
            <Heart size={28} />
          </button>
        </div>
      )}
    </div>
  );
}
